import {useRef, useCallback, useEffect, useState} from 'react'
import type {BarcodeDetector} from '../../../../../types/barcodeTypes'

export interface BarcodeDetectionResult {
  detected: true
  corners: {x: number; y: number}[]
  centerY: number
}

// Frame overlay dimensions (80% of video dimensions, centered with 10% margin on each side)
const FRAME_REGION = {xRatio: 0.1, yRatio: 0.1, widthRatio: 0.8, heightRatio: 0.8}
// Expected barcode position: 82% down from top of frame (based on USPS label layout)
const EXPECTED_BARCODE_Y_IN_FRAME = 0.82
// Position tolerance: ±5% to account for slight label positioning variations
const BARCODE_POSITION_TOLERANCE = 0.05
// Expected barcode dimensions relative to frame (based on USPS label standard)
const EXPECTED_BARCODE_WIDTH_RATIO = 0.35
const EXPECTED_BARCODE_HEIGHT_RATIO = 0.15
// Size tolerance: ±10% to account for distance variations
const BARCODE_SIZE_TOLERANCE = 0.1

interface LabelDetectorProps {
  videoRef: React.RefObject<HTMLVideoElement>
  onDetected: (result: BarcodeDetectionResult) => void
  isActive: boolean
}

export function useLabelDetector({videoRef, onDetected, isActive}: LabelDetectorProps) {
  const detectionCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const barcodeDetectorRef = useRef<BarcodeDetector | null>(null)
  const [hasDetector, setHasDetector] = useState(true)

  useEffect(() => {
    detectionCanvasRef.current = document.createElement('canvas')

    if ('BarcodeDetector' in window && window.BarcodeDetector) {
      try {
        barcodeDetectorRef.current = new window.BarcodeDetector()
      } catch {
        setHasDetector(false)
      }
    } else {
      setHasDetector(false)
    }

    return () => {
      // Clean up canvas to prevent memory leaks
      if (detectionCanvasRef.current) {
        const ctx = detectionCanvasRef.current.getContext('2d')
        if (ctx) {
          ctx.clearRect(0, 0, detectionCanvasRef.current.width, detectionCanvasRef.current.height)
        }
        detectionCanvasRef.current.width = 0
        detectionCanvasRef.current.height = 0
        detectionCanvasRef.current = null
      }
    }
  }, [])

  const detect = useCallback(async () => {
    if (!isActive || !videoRef.current || !detectionCanvasRef.current) return null

    const video = videoRef.current
    const canvas = detectionCanvasRef.current

    if (video.videoWidth === 0 || video.videoHeight === 0) return null

    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
    }

    const ctx = canvas.getContext('2d', {willReadFrequently: true})
    if (!ctx) return null

    ctx.drawImage(video, 0, 0)

    if (barcodeDetectorRef.current) {
      try {
        const barcodes = await barcodeDetectorRef.current.detect(canvas)

        if (barcodes && barcodes.length > 0) {
          const barcode = barcodes[0]
          const corners = barcode.cornerPoints || []

          // Barcodes have 4 corner points defining their boundaries
          if (corners.length === 4) {
            const minX = Math.min(...corners.map((p) => p.x))
            const maxX = Math.max(...corners.map((p) => p.x))
            const minY = Math.min(...corners.map((p) => p.y))
            const maxY = Math.max(...corners.map((p) => p.y))

            const barcodeWidth = maxX - minX
            const barcodeHeight = maxY - minY
            const centerY = (minY + maxY) / 2
            const centerX = (minX + maxX) / 2

            const frameWidth = canvas.width * FRAME_REGION.widthRatio
            const frameHeight = canvas.height * FRAME_REGION.heightRatio
            const frameLeft = canvas.width * FRAME_REGION.xRatio
            const frameTop = canvas.height * FRAME_REGION.yRatio
            const frameRight = frameLeft + frameWidth
            const frameBottom = frameTop + frameHeight
            const frameCenterX = frameLeft + frameWidth / 2

            const isInsideFrame =
              minX >= frameLeft && maxX <= frameRight && minY >= frameTop && maxY <= frameBottom

            const barcodeWidthRatio = barcodeWidth / frameWidth
            const barcodeHeightRatio = barcodeHeight / frameHeight
            const barcodeYRatio = (centerY - frameTop) / frameHeight
            const centerXOffset = Math.abs(centerX - frameCenterX) / frameWidth

            const widthError =
              Math.abs(barcodeWidthRatio - EXPECTED_BARCODE_WIDTH_RATIO) /
              EXPECTED_BARCODE_WIDTH_RATIO
            const heightError =
              Math.abs(barcodeHeightRatio - EXPECTED_BARCODE_HEIGHT_RATIO) /
              EXPECTED_BARCODE_HEIGHT_RATIO
            const positionError = Math.abs(barcodeYRatio - EXPECTED_BARCODE_Y_IN_FRAME)

            if (!isInsideFrame) return null

            // Horizontal center offset tolerance: ±10% to ensure barcode is centered in frame
            if (
              widthError <= BARCODE_SIZE_TOLERANCE &&
              heightError <= BARCODE_SIZE_TOLERANCE &&
              positionError <= BARCODE_POSITION_TOLERANCE &&
              centerXOffset <= 0.1
            ) {
              const result: BarcodeDetectionResult = {
                detected: true,
                corners: corners.map((p) => ({x: p.x, y: p.y})),
                centerY,
              }
              onDetected(result)
              return result
            }
          }
        }
      } catch {}
    }

    return null
  }, [isActive, videoRef, onDetected])

  return {detect, hasDetector}
}
